import { DashboardWorkspaceFrame } from '@shigo-ui/dashboard-ui';
import { applyBrand, applyTheme, brands, type BrandId } from '@shigo-ui/theme';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  FadeIn,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@shigo-ui/ui';
import { useEffect, useState } from 'react';

import { dashboardT } from '../lib/messages';

const brandIds = Object.keys(brands) as BrandId[];
const themeMessageKeys = {
  light: 'designSystem.theme.light',
  dark: 'designSystem.theme.dark',
} as const;

export function DesignSystemPage() {
  const [brand, setBrand] = useState<BrandId>('default');
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    applyBrand(brand);
  }, [brand]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return (
    <main className="bg-background text-foreground min-h-dvh">
      <FadeIn>
        <div className="mx-auto max-w-6xl space-y-10 px-6 py-12">
          <header className="space-y-3">
            <Badge>{dashboardT('designSystem.badge')}</Badge>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              {dashboardT('designSystem.title')}
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              {dashboardT('designSystem.description')}
            </p>
            <div className="flex flex-wrap gap-3">
              {brandIds.map((id) => (
                <Button
                  key={id}
                  variant={brand === id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setBrand(id);
                  }}
                >
                  {brands[id].label}
                </Button>
              ))}
              <Button
                variant={theme === 'dark' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setTheme(theme === 'light' ? 'dark' : 'light');
                }}
              >
                {dashboardT('designSystem.theme.toggle', {
                  theme: dashboardT(themeMessageKeys[theme]),
                })}
              </Button>
            </div>
          </header>

          <Tabs defaultValue="forms">
            <TabsList>
              <TabsTrigger value="forms">{dashboardT('designSystem.tab.forms')}</TabsTrigger>
              <TabsTrigger value="feedback">{dashboardT('designSystem.tab.feedback')}</TabsTrigger>
            </TabsList>
            <TabsContent value="forms" className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{dashboardT('designSystem.inputs.title')}</CardTitle>
                  <CardDescription>{dashboardT('designSystem.inputs.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{dashboardT('designSystem.inputs.email')}</Label>
                    <Input
                      id="email"
                      placeholder={dashboardT('designSystem.inputs.emailPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">{dashboardT('designSystem.inputs.notes')}</Label>
                    <Textarea
                      id="notes"
                      placeholder={dashboardT('designSystem.inputs.notesPlaceholder')}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="terms" />
                    <Label htmlFor="terms">{dashboardT('designSystem.inputs.acceptTerms')}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="notifications" />
                    <Label htmlFor="notifications">
                      {dashboardT('designSystem.inputs.notifications')}
                    </Label>
                  </div>
                  <Select defaultValue="a">
                    <SelectTrigger>
                      <SelectValue
                        placeholder={dashboardT('designSystem.inputs.selectPlaceholder')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="a">{dashboardT('designSystem.inputs.optionA')}</SelectItem>
                      <SelectItem value="b">{dashboardT('designSystem.inputs.optionB')}</SelectItem>
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{dashboardT('designSystem.actions.title')}</CardTitle>
                  <CardDescription>
                    {dashboardT('designSystem.actions.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  <Button>{dashboardT('designSystem.actions.primary')}</Button>
                  <Button variant="outline">{dashboardT('designSystem.actions.outline')}</Button>
                  <Button variant="ghost">{dashboardT('designSystem.actions.ghost')}</Button>
                  <Badge>{dashboardT('designSystem.actions.default')}</Badge>
                  <Badge variant="secondary">{dashboardT('designSystem.actions.secondary')}</Badge>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="feedback">
              <Card>
                <CardHeader>
                  <CardTitle>{dashboardT('designSystem.loading')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold tracking-normal">
              {dashboardT('designSystem.composition.title')}
            </h2>
            <div className="h-[28rem] overflow-hidden rounded-lg border">
              <DashboardWorkspaceFrame
                workspaceTitle={dashboardT('designSystem.composition.workspaceTitle')}
                statusLabel={dashboardT('designSystem.composition.statusReady')}
                statusTone="success"
                chat={
                  <aside className="border-border bg-card border-r p-4">
                    <p className="text-sm font-medium">
                      {dashboardT('designSystem.composition.activity')}
                    </p>
                    <p className="text-muted-foreground mt-2 text-sm">
                      {dashboardT('designSystem.composition.activityDescription')}
                    </p>
                  </aside>
                }
                canvas={
                  <section className="shigo-grid bg-background flex items-center justify-center p-6">
                    <Card className="max-w-sm">
                      <CardHeader>
                        <CardTitle>{dashboardT('designSystem.composition.canvas')}</CardTitle>
                        <CardDescription>
                          {dashboardT('designSystem.composition.canvasDescription')}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  </section>
                }
              />
            </div>
          </section>
        </div>
      </FadeIn>
    </main>
  );
}
